import classNames from "classnames";
import Modal from "components/DesignSystem/Modal/Modal";
import { Stepper } from "components/DesignSystem/Stepper/Stepper";
import { ArrowRight } from "components/icons/ArrowRight";
import { BroadcastFormValues } from "components/UnifiedBroadcast/UnifiedBroadcast";
import { useFormikContext } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { ModalProps } from "types/utils/modal";
import { capitalize } from "utils/capitalize";
import { BroadcastMessageTemplateBody } from "./BroadcastMessageTemplateBody";
import { TemplateTypes } from "types/Models/broadcast";
import {
  BROADCAST_CHAT_TEMPLATE,
  BROADCAST_EMAIL_TEMPLATE,
  BROADCAST_IVR_TEMPLATE,
  BROADCAST_VIA_DISPLAY,
  BROADCAST_WHATSAPP_TEMPLATE,
} from "constants/broadcast";
import { BroadcastCallConfig } from "components/BroadcastCall/BroadcastCallConfig";

export const BroadcastMessageTemplates = ({
  isOpen,
  close,
  templateFields,
  setTemplateFields,
  openSendBroadcast,
  ivrCSVFile,
}: ModalProps & {
  templateFields: Record<string, Record<string, any> | string>;
  setTemplateFields: React.Dispatch<
    React.SetStateAction<Record<string, Record<string, any> | string>>
  >;
  openSendBroadcast: () => void;
  ivrCSVFile: File | null;
}) => {
  const [selectedStates, setSelectedStates] = useState<TemplateTypes[]>([]);
  const [currentTemplateType, setCurrentTemplateType] = useState<number>(1);
  const { values } = useFormikContext<BroadcastFormValues>();
  const {
    broadcastViaChat,
    broadcastViaEmail,
    broadcastViaWhatsapp,
    broadcastViaIvrCall,
  } = values;

  useEffect(() => {
    const selectedTemplates = [];
    if (broadcastViaChat) {
      selectedTemplates.push(BROADCAST_CHAT_TEMPLATE);
    }
    if (broadcastViaWhatsapp) {
      selectedTemplates.push(BROADCAST_WHATSAPP_TEMPLATE);
    }
    if (broadcastViaEmail) {
      selectedTemplates.push(BROADCAST_EMAIL_TEMPLATE);
    }
    if (broadcastViaIvrCall) {
      selectedTemplates.push(BROADCAST_IVR_TEMPLATE);
    }
    setSelectedStates(selectedTemplates as TemplateTypes[]);
  }, [
    broadcastViaChat,
    broadcastViaEmail,
    broadcastViaWhatsapp,
    broadcastViaIvrCall,
  ]);

  const templateSteps = useMemo(() => {
    return selectedStates.map((template, index) => ({
      step: index + 1,
      name: BROADCAST_VIA_DISPLAY[template],
    }));
  }, [selectedStates]);

  const handleGoBack = () => {
    if (currentTemplateType > 1) {
      setCurrentTemplateType(currentTemplateType - 1);
    } else {
      close();
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content size="xl">
        <Modal.Header>
          <div>
            <Modal.Title>Broadcast Message</Modal.Title>
            <Modal.Subtitle className="t-mt-2">
              {templateSteps.length > 1 && (
                <Stepper size="xs" direction="horizontal">
                  {templateSteps.map(({ step, name }) => (
                    <Stepper.Step
                      step={step}
                      key={step}
                      isActive={currentTemplateType >= step}
                      clickable={currentTemplateType >= step}
                      onClick={() => setCurrentTemplateType(step)}
                    >
                      <div
                        className={classNames(
                          "t-flex t-items-center t-text-body-sm",
                          {
                            "t-text-text-30": currentTemplateType < step,
                            "t-text-purple": currentTemplateType >= step,
                          }
                        )}
                      >
                        {name}
                        {step !== templateSteps.length && (
                          <ArrowRight color="currentColor" />
                        )}
                      </div>
                    </Stepper.Step>
                  ))}
                </Stepper>
              )}
            </Modal.Subtitle>
          </div>
          <Modal.Close />
        </Modal.Header>
        {selectedStates[currentTemplateType - 1] === BROADCAST_IVR_TEMPLATE ? (
          <BroadcastCallConfig
            uploadedCSV={ivrCSVFile}
            handleGoBack={handleGoBack}
            currentTemplateType={currentTemplateType}
            selectedStatesLength={selectedStates.length}
            setCurrentTemplateType={setCurrentTemplateType}
            openSendBroadcast={openSendBroadcast}
            setTemplateFields={setTemplateFields}
            templateFields={templateFields}
          />
        ) : (
          <BroadcastMessageTemplateBody
            type={selectedStates[currentTemplateType - 1]}
            handleGoBack={handleGoBack}
            currentTemplateType={currentTemplateType}
            selectedStatesLength={selectedStates.length}
            setCurrentTemplateType={setCurrentTemplateType}
            openSendBroadcast={openSendBroadcast}
            setTemplateFields={setTemplateFields}
            templateFields={templateFields}
          />
        )}
      </Modal.Content>
    </Modal.Root>
  );
};
